import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Check,
  ArrowRight,
  ChevronRight,
  User,
  Briefcase,
  GraduationCap,
  Target,
  FileText,
  Globe,
  Code,
  Layers,
  Heart,
  Eye,
} from 'lucide-react'
import { getCandidateProfileStats } from '@/services/api'
import { useAuth } from '@/stores/authStore'
import type { ProfileStatsSection, ProfileStatsField } from '@/types'

// Maps section IDs to profile edit tab hashes.
const SECTION_TO_TAB: Record<string, string> = {
  basic_info: 'basic',
  basic: 'basic',
  work_experience: 'experience',
  experience: 'experience',
  education: 'education',
  skills: 'skills',
  language: 'languages',
  languages: 'languages',
  spoken_language: 'languages',
  spoken_languages: 'languages',
  documents: 'documents',
  cv: 'documents',
  resume: 'documents',
  projects: 'projects',
  job_preferences: 'jobPreferences',
  jobPreferences: 'jobPreferences',
  preferences: 'jobPreferences',
}

// Maps individual field IDs to their own tab.
// The backend bundles languages inside the skills section and education
// inside the experience section, so field-level routing is required.
const FIELD_TO_TAB: Record<string, string> = {
  // basic info fields
  name: 'basic',
  phone: 'basic',
  location: 'basic',
  job_title: 'basic',
  bio: 'basic',
  photo: 'basic',
  // experience section fields
  years_experience: 'experience',
  work_experience: 'experience',
  education: 'education',
  // skills section fields — languages live here in the API response
  skills: 'skills',
  languages: 'languages',
  language: 'languages',
  // documents section fields
  cv: 'documents',
  resume: 'documents',
  portfolio: 'documents',
  // projects
  projects: 'projects',
}

const SECTION_ICONS: Record<string, React.ReactNode> = {
  basic_info: <User className="w-3.5 h-3.5" />,
  basic: <User className="w-3.5 h-3.5" />,
  work_experience: <Briefcase className="w-3.5 h-3.5" />,
  experience: <Briefcase className="w-3.5 h-3.5" />,
  education: <GraduationCap className="w-3.5 h-3.5" />,
  skills: <Target className="w-3.5 h-3.5" />,
  language: <Globe className="w-3.5 h-3.5" />,
  languages: <Globe className="w-3.5 h-3.5" />,
  documents: <FileText className="w-3.5 h-3.5" />,
  cv: <FileText className="w-3.5 h-3.5" />,
  projects: <Code className="w-3.5 h-3.5" />,
  job_preferences: <Heart className="w-3.5 h-3.5" />,
  jobPreferences: <Heart className="w-3.5 h-3.5" />,
}

// Regex fallback: infer tab from a human-readable label when the id is
// not in a lookup table (covers multilingual labels and unknown IDs).
const LABEL_TAB_PATTERNS: Array<[RegExp, string]> = [
  [/langua|langue|lingu|لغ/i, 'languages'],
  [/skill|compéten|مهار/i, 'skills'],
  [/experi|expéri|خبر|عمل/i, 'experience'],
  [/educat|formation|تعليم|دراس/i, 'education'],
  [/document|cv|resume|سيرة/i, 'documents'],
  [/project|projet|مشروع/i, 'projects'],
  [/preferen|préfér|تفضيل/i, 'jobPreferences'],
]

function resolveTab(id: string, label: string, lookup: Record<string, string>): string | null {
  if (lookup[id]) return lookup[id]
  for (const [pattern, tab] of LABEL_TAB_PATTERNS) {
    if (pattern.test(label)) return tab
  }
  return null
}

function getSectionUrl(sectionId: string, label: string): string {
  const tab = resolveTab(sectionId, label, SECTION_TO_TAB) ?? 'basic'
  return `/candidate/profile/edit#${tab}`
}

// Each field gets its own URL so fields grouped under a shared section
// (e.g. "languages" inside the "skills" section) still land on the right tab.
function getFieldUrl(field: ProfileStatsField, fallbackUrl: string): string {
  const tab = resolveTab(field.id, field.label, FIELD_TO_TAB)
  return tab ? `/candidate/profile/edit#${tab}` : fallbackUrl
}

function getSectionIcon(sectionId: string): React.ReactNode {
  return SECTION_ICONS[sectionId] ?? <Layers className="w-3.5 h-3.5" />
}

interface SectionRowProps {
  section: ProfileStatsSection
  onNavigate: (url: string) => void
}

function SectionRow({ section, onNavigate }: SectionRowProps) {
  const { t } = useTranslation('dashboard')
  const sectionUrl = getSectionUrl(section.id, section.label)
  const incompleteFields = section.fields.filter((f: ProfileStatsField) => !f.completed)

  return (
    <div>
      {/* Section row — always clickable */}
      <button
        onClick={() => onNavigate(sectionUrl)}
        className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors group"
        aria-label={section.label}
      >
        <div
          className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center ${
            section.completed ? 'bg-green-100' : 'bg-gray-100'
          }`}
          aria-hidden="true"
        >
          {section.completed ? (
            <Check className="w-3 h-3 text-green-600" />
          ) : (
            <span className="text-gray-400">{getSectionIcon(section.id)}</span>
          )}
        </div>

        <span
          className={`flex-1 text-sm text-left ${
            section.completed
              ? 'text-gray-400 line-through decoration-gray-300'
              : 'text-gray-900 font-medium'
          }`}
        >
          {section.label}
        </span>

        {!section.completed && (
          <span className="text-xs text-gray-400 tabular-nums flex-shrink-0 rtl:mr-auto">
            {t('candidate.profileCompletion.widget.fieldCount', {
              completed: section.completedFields,
              total: section.totalFields,
            })}
          </span>
        )}

        <ChevronRight
          className="w-3.5 h-3.5 text-gray-300 group-hover:text-gray-500 flex-shrink-0 transition-colors rtl:rotate-180"
          aria-hidden="true"
        />
      </button>

      {/* Incomplete fields — each routes to its own specific tab */}
      {!section.completed && incompleteFields.length > 0 && (
        <div className="ms-8 mb-1 space-y-0.5">
          {incompleteFields.map((field: ProfileStatsField) => (
            <button
              key={field.id}
              onClick={() => onNavigate(getFieldUrl(field, sectionUrl))}
              className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-blue-50 transition-colors group text-left"
            >
              <span
                className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-amber-400"
                aria-hidden="true"
              />
              <span className="text-xs text-gray-500 group-hover:text-blue-600 transition-colors flex-1">
                {field.label}
              </span>
              <ArrowRight
                className="w-3 h-3 text-blue-400 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity rtl:rotate-180"
                aria-hidden="true"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default function ProfileCompletionWidget() {
  const navigate = useNavigate()
  const { t } = useTranslation('dashboard')
  const { user } = useAuth()

  const { data, isLoading, isError } = useQuery({
    queryKey: ['candidateProfileStats'],
    queryFn: getCandidateProfileStats,
    enabled: user?.dataProcessingConsent === true,
  })

  if (isLoading) {
    return (
      <div className="bg-white/70 backdrop-blur-sm border border-gray-200 rounded-2xl shadow-lg p-5 animate-pulse">
        <div className="flex items-center justify-between mb-3">
          <div className="h-4 bg-gray-200 rounded w-44" />
          <div className="h-7 bg-gray-200 rounded w-28" />
        </div>
        <div className="h-2 bg-gray-200 rounded-full mb-4" />
        <div className="space-y-2">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-9 bg-gray-100 rounded-lg" />
          ))}
        </div>
      </div>
    )
  }

  if (isError || !data) return null

  const { completionPercentage, sections, profileViews } = data
  const completedCount = sections.filter((s) => s.completed).length
  const statusKey =
    completionPercentage === 100
      ? 'complete'
      : completionPercentage >= 80
      ? 'almostThere'
      : completionPercentage >= 50
      ? 'goodProgress'
      : 'getStarted'
  const barColor =
    completionPercentage >= 80
      ? 'from-green-400 to-green-500'
      : completionPercentage >= 50
      ? 'from-blue-500 to-purple-500'
      : 'from-amber-400 to-orange-400'

  return (
    <div className="bg-white/70 backdrop-blur-sm border border-gray-200 rounded-2xl shadow-lg overflow-hidden">
      <div className="h-1 bg-gradient-to-r from-blue-500 to-purple-500" />

      <div className="p-5">
        {/* Compact header: progress bar + percentage + CTA */}
        <div className="flex items-center gap-3 mb-1">
          <div className="flex-1">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-sm font-semibold text-gray-900">
                {t('candidate.profileCompletion.title')}
              </span>
              <span
                className={`text-sm font-bold tabular-nums ${
                  completionPercentage >= 80
                    ? 'text-green-600'
                    : completionPercentage >= 50
                    ? 'text-blue-600'
                    : 'text-amber-600'
                }`}
              >
                {completionPercentage}%
              </span>
            </div>
            <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full bg-gradient-to-r ${barColor} transition-all duration-700`}
                style={{ width: `${completionPercentage}%` }}
              />
            </div>
          </div>

          {completionPercentage < 100 && (
            <button
              onClick={() => navigate('/candidate/profile/edit')}
              className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-medium transition-colors shadow group"
            >
              {t('candidate.profileCompletion.cta')}
              <ArrowRight
                className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform rtl:rotate-180"
                aria-hidden="true"
              />
            </button>
          )}
        </div>

        <div className="flex items-center justify-between mb-3 ps-0.5">
          <p className="text-xs text-gray-500">
            {t(`candidate.profileCompletion.${statusKey}`)}
            {' '}
            <span className="text-gray-400">
              ({completedCount}/{sections.length})
            </span>
          </p>
          {profileViews != null && (
            <span className="flex items-center gap-1 text-xs text-gray-400">
              <Eye className="w-3.5 h-3.5" />
              {profileViews}
            </span>
          )}
        </div>

        {/* Checklist — hidden when profile is complete */}
        {completionPercentage < 100 && (
          <div className="space-y-0.5" role="list" aria-label={t('candidate.profileCompletion.widget.sectionsLabel')}>
            {sections.map((section) => (
              <SectionRow key={section.id} section={section} onNavigate={navigate} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
